import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const LOGO_ICON = "/logo-emblem.png";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  // Already logged in? Skip straight to the Vault Office.
  useEffect(() => {
    const t = localStorage.getItem("sx_admin_token");
    if (!t) return;
    api.get("/auth/me")
      .then(() => navigate("/admin", { replace: true }))
      .catch(() => localStorage.removeItem("sx_admin_token"));
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", form);
      localStorage.setItem("sx_admin_token", data.token);
      toast.success("Welcome back to the vault");
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiError(err, "Login failed"));
    } finally {
      setLoading(false);
    }
  };

  const sendReset = async (e) => {
    e.preventDefault();
    setForgotBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail });
      setForgotSent(true);
    } catch (err) {
      toast.error(formatApiError(err, "Could not send the reset email"));
    } finally {
      setForgotBusy(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#F3EDE3] flex items-center justify-center px-6" data-testid="admin-login-page">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md bg-white border border-[#DCD3C3] p-10"
      >
        <div className="flex items-center gap-3">
          <img src={LOGO_ICON} alt="Stud Exchange" className="w-12 h-12 rounded-full object-cover border border-[#DCD3C3]" />
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#B38728]">Admin Access</p>
            <h1 className="font-serif text-2xl">The Vault Office</h1>
          </div>
        </div>
        <form onSubmit={submit} className="mt-8 space-y-5">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#5C564C]">Email</label>
            <input
              type="email"
              required
              data-testid="admin-login-email-input"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-2 w-full bg-[#F3EDE3] border border-[#DCD3C3] px-4 py-3 text-sm focus:outline-none focus:border-[#B38728] transition-colors"
            />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#5C564C]">Password</label>
            <input
              type="password"
              required
              data-testid="admin-login-password-input"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="mt-2 w-full bg-[#F3EDE3] border border-[#DCD3C3] px-4 py-3 text-sm focus:outline-none focus:border-[#B38728] transition-colors"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit-button"
            className="w-full bg-[#0A0A0A] text-[#F3EDE3] py-4 text-xs font-mono uppercase tracking-[0.3em] hover:bg-[#B38728] hover:text-[#0A0A0A] transition-colors disabled:opacity-50"
          >
            {loading ? "Unlocking…" : "Enter the Vault"}
          </button>
        </form>
        <div className="mt-5 text-center">
          <button
            type="button"
            onClick={() => { setForgotOpen((v) => !v); setForgotSent(false); }}
            data-testid="forgot-password-link"
            className="text-xs text-[#5C564C] hover:text-[#B38728] transition-colors underline underline-offset-4 decoration-[#DCD3C3]"
          >
            Forgot your password?
          </button>
        </div>
        {forgotOpen && (
          <div className="mt-4 border-t border-[#DCD3C3] pt-5" data-testid="forgot-password-panel">
            {forgotSent ? (
              <p className="text-sm text-[#5C564C] text-center" data-testid="forgot-password-success">
                If that's the admin email, a reset link is on its way — check your inbox. The link works once and expires in 1 hour.
              </p>
            ) : (
              <form onSubmit={sendReset} className="flex gap-2">
                <input
                  type="email"
                  required
                  placeholder="Your admin email"
                  data-testid="forgot-password-email-input"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  className="flex-1 bg-[#F3EDE3] border border-[#DCD3C3] px-4 py-3 text-sm focus:outline-none focus:border-[#B38728] transition-colors"
                />
                <button
                  type="submit"
                  disabled={forgotBusy}
                  data-testid="forgot-password-submit-button"
                  className="bg-[#0A0A0A] text-[#F3EDE3] px-5 text-xs font-mono uppercase tracking-[0.2em] hover:bg-[#B38728] hover:text-[#0A0A0A] transition-colors disabled:opacity-50"
                >
                  {forgotBusy ? "…" : "Send Link"}
                </button>
              </form>
            )}
          </div>
        )}
        <Link to="/" className="block mt-6 text-center text-xs text-[#5C564C] hover:text-[#B38728] transition-colors" data-testid="admin-login-back-link">
          ← Back to storefront
        </Link>
      </motion.div>
    </main>
  );
}
